import React, { useState, ReactNode, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  type CanonicalTheme as CanonicalThemeType,
  getInitialTheme,
  getSystemTheme,
  persistThemePreference,
} from '@/theme/theme-utils';
import { IconProvider } from '../IconContext/IconContext';
import { ThemeContext } from './useTheme';

// Using shared theme utilities. No legacy aliasing retained.

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // Initialize from storage on the client, fall back to 'device' on SSR.
  // This keeps the first client render aligned with the inline theme script.
  const [currentTheme, setCurrentTheme] = useState<CanonicalThemeType>(() => getInitialTheme());

  // Temporary theme for UI previews
  const [temporaryTheme, setTemporaryThemeState] = useState<CanonicalThemeType | null>(null);
  // Track current system theme to resolve "device" preference and listen for changes
  // Initialize from matchMedia on the client (falls back to 'light' on SSR).
  const [systemTheme, setSystemTheme] = useState<CanonicalThemeType>(() => getSystemTheme());

  // Track if theme has been loaded from storage to prevent overwriting with default
  const [isThemeLoaded] = useState(() => typeof window !== 'undefined');
  const shouldAnimateRef = useRef(false);
  const hasAppliedThemeRef = useRef(false);

  // The displayed (applied) theme for DOM. Resolves "device" to current system theme.
  const selectedTheme = temporaryTheme ?? currentTheme;
  const theme = selectedTheme === 'device' ? systemTheme : selectedTheme;

  const isTemporaryTheme = temporaryTheme !== null;

  // Listen for system theme changes. Always update systemTheme; applied theme
  // will re-compute if current preference is "device".
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    // Initialize in case matchMedia state differs from initial getSystemTheme
    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Apply theme to DOM
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    // Apply resolved theme to DOM
    document.documentElement.setAttribute('data-theme', theme);

    // Lazy load theme CSS for non-standard themes
    if (theme === 'buzzfeed') import('@/themes/buzzfeed.css');
    else if (theme === 'history') import('@/themes/history.css');
    else if (theme === 'indyweek') import('@/themes/indyweek.css');
    else if (theme === 'assembly') import('@/themes/assembly.css');

    if (!hasAppliedThemeRef.current) {
      document.documentElement.setAttribute('data-theme-ready', 'true');
      hasAppliedThemeRef.current = true;
    }

    const shouldAnimate = shouldAnimateRef.current;
    shouldAnimateRef.current = false;

    if (!shouldAnimate) {
      document.documentElement.classList.remove('theme-transition');
      return;
    }

    document.documentElement.classList.add('theme-transition');
    const cleanup = setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
    }, 200);

    return () => clearTimeout(cleanup);
  }, [theme]);

  // Persist the user's preference (including 'device') unless a runtime preview is active.
  useEffect(() => {
    if (!temporaryTheme && isThemeLoaded) {
      persistThemePreference(currentTheme);
    }
  }, [currentTheme, temporaryTheme, isThemeLoaded]);

  const toggleTheme = useCallback(() => {
    // Cycle through Device -> Light -> Dark -> Device
    shouldAnimateRef.current = true;
    setTemporaryThemeState(null);
    const nextTheme: CanonicalThemeType =
      currentTheme === 'device' ? 'light' : currentTheme === 'light' ? 'dark' : 'device';
    setCurrentTheme(nextTheme);
  }, [currentTheme]);

  const setTheme = useCallback((newTheme: CanonicalThemeType) => {
    // Apply the chosen theme and end any temporary preview.
    shouldAnimateRef.current = true;
    setTemporaryThemeState(null);
    setCurrentTheme(newTheme);
  }, []);

  const setTemporaryTheme = useCallback((newTheme: CanonicalThemeType) => {
    shouldAnimateRef.current = true;
    setTemporaryThemeState(newTheme);
  }, []);

  const clearTemporaryTheme = useCallback(() => {
    shouldAnimateRef.current = true;
    setTemporaryThemeState(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      theme,
      preference: currentTheme,
      toggleTheme,
      setTheme,
      setTemporaryTheme,
      clearTemporaryTheme,
      isTemporaryTheme,
    }),
    [
      theme,
      currentTheme,
      toggleTheme,
      setTheme,
      setTemporaryTheme,
      clearTemporaryTheme,
      isTemporaryTheme,
    ]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <IconProvider>{children}</IconProvider>
    </ThemeContext.Provider>
  );
};
